import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { safeRedirect, rememberPostAuthRedirect } from '../utils/auth';
import '../styles/auth.css';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="#1877F2" d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.51 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.57V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z"/>
  </svg>
);

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  // Staff mode arrives from the School portal (?role=staff). Strips
  // sign-up + social options because teachers/school admins are created
  // by an admin and never self-register.
  const isStaff = new URLSearchParams(location.search).get('role') === 'staff';
  const initialMode = !isStaff && location.pathname.includes('register') ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  // Staff sub-step: 'request' → email + school code submission, then
  // 'verify' → 6-digit OTP. Only used when isStaff.
  const [staffStep, setStaffStep] = useState('request');
  const [schoolCode, setSchoolCode] = useState('');
  const [staffOtp, setStaffOtp] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => ({
    oauth_existing_account: 'This email already has a DeenLit account. Sign in using the method you used when registering. If you registered with a password, enter it below or use Forgot password.',
    oauth_unverified_email: 'Google could not confirm your email address. Verify it with Google, then try again.',
    oauth_failed: 'We could not finish signing you in. Please start again using the sign-in button below.',
    google: 'Google sign-in was cancelled or expired. Please try again.',
    facebook: 'Facebook sign-in was cancelled or expired. Please try again.',
  }[new URLSearchParams(location.search).get('error')] || ''));

  const isRegister = mode === 'register';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleStaffRequest = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email) return setError('Enter your school email.');
    const code = schoolCode.trim().toUpperCase();
    if (code.length !== 6) return setError('School code is 6 letters and numbers.');

    setLoading(true);
    try {
      await axios.post('/api/auth/staff/request-otp', { email: form.email, schoolCode: code });
      // Generic success — server doesn't tell us whether the pair was
      // valid. Either way we advance to the OTP step; if the pair was
      // wrong the code never arrives.
      setStaffStep('verify');
      setStaffOtp('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send code. Try again.');
    } finally { setLoading(false); }
  };

  const handleStaffVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(staffOtp)) return setError('Code is 6 digits.');

    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/staff/verify-otp', {
        email: form.email, otp: staffOtp,
      });
      if (data?.token) localStorage.setItem('adeeb-token', data.token);
      if (data?.user)  localStorage.setItem('adeeb-user', JSON.stringify(data.user));
      window.dispatchEvent(new Event('adeeb-auth-change'));
      // Tag the session as "staff mode" — we came in via the staff
      // OTP form. Used by StaffRedirectGuard so the parent flow's
      // free-rein doesn't apply here.
      localStorage.setItem('adeeb-session-mode', 'staff');
      const accountType = data?.user?.accountType || 'parent';
      const defaultPath =
        accountType === 'school'  ? '/teacher' :
        accountType === 'teacher' ? '/teacher' :
                                    '/profiles/select';
      navigate(defaultPath, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not verify code.');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    if (isRegister) {
      if (!form.name.trim()) return setError('Please enter your name.');
      if (form.password.length < 6) return setError('Password must be at least 6 characters.');
      if (form.password !== form.confirm) return setError('Passwords do not match.');
    }

    setLoading(true);
    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const payload = isRegister
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const { data } = await axios.post(endpoint, payload);
      if (data?.token) localStorage.setItem('adeeb-token', data.token);
      if (data?.user) localStorage.setItem('adeeb-user', JSON.stringify(data.user));
      window.dispatchEvent(new Event('adeeb-auth-change'));
      if (isRegister && data?.user && !data.user.emailVerified) {
        rememberPostAuthRedirect(new URLSearchParams(location.search).get('redirect'));
        navigate('/verify-pending', { replace: true });
        return;
      }
      const redirect = new URLSearchParams(location.search).get('redirect');
      if (data?.user && data.user.emailVerified === false) {
        navigate('/verify-pending', { replace: true });
        return;
      }
      // Tag the session as "parent mode" so the staff redirect guard
      // doesn't bounce us off /profiles/select / /settings even when
      // the User row has accountType school/teacher (same email used
      // for both flows).
      localStorage.setItem('adeeb-session-mode', 'parent');
      navigate(safeRedirect(redirect) || '/profiles/select');
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = (provider) => {
    const apiBase = window.location.origin;
    window.location.href = `${apiBase}/api/auth/${provider}`;
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    const redirect = new URLSearchParams(location.search).get('redirect');
    const search = safeRedirect(redirect) ? `?redirect=${encodeURIComponent(redirect)}` : '';
    navigate((next === 'login' ? '/login' : '/register') + search, { replace: true });
  };

  return (
    <div className="auth-wrapper">
      <Link to={isStaff ? '/school-portal' : '/'} className="floating-back-btn">
        &#8592; {isStaff ? 'School portal' : 'Homepage'}
      </Link>

      <div className="auth-bg-ornament" aria-hidden="true">&#10040; &#10040; &#10040;</div>

      <div className="auth-card">
        <Link to="/" className="auth-logo"><img className="auth-brand-image" src="/branding/deenlit-logo.png" alt="DeenLit" width="2172" height="724" /></Link>
        <p className="auth-tagline">
          {isStaff ? 'Teacher / school admin sign in' : 'A World of Stories Awaits'}
        </p>

        {!isStaff && (
          <div className="auth-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={!isRegister}
              className={`auth-tab ${!isRegister ? 'is-active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Sign in
            </button>
            <button
              role="tab"
              aria-selected={isRegister}
              className={`auth-tab ${isRegister ? 'is-active' : ''}`}
              onClick={() => switchMode('register')}
            >
              Create account
            </button>
            <span className={`auth-tab-indicator ${isRegister ? 'right' : 'left'}`} />
          </div>
        )}

        {!isStaff && (
          <>
            <div className="auth-social">
              <button type="button" className="auth-social-btn" onClick={() => handleSocial('google')}>
                <GoogleIcon />
                <span>Continue with Google</span>
              </button>
              <button type="button" className="auth-social-btn" onClick={() => handleSocial('facebook')}>
                <FacebookIcon />
                <span>Continue with Facebook</span>
              </button>
            </div>

            <div className="auth-divider"><span>or</span></div>
          </>
        )}

        {!isStaff && (
          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            {isRegister && (
              <label className="auth-field">
                <span>Name</span>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Your name"
                  value={form.name}
                  onChange={handleChange}
                />
              </label>
            )}

            <label className="auth-field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </label>

            <label className="auth-field">
              <span>Password</span>
              <div className="auth-password">
                <input
                  aria-label="Password" type={showPw ? 'text' : 'password'}
                  name="password"
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  placeholder={isRegister ? 'At least 6 characters' : '••••••••'}
                  value={form.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="auth-show-pw"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>

            {isRegister && (
              <label className="auth-field">
                <span>Confirm password</span>
                <input
                  type={showPw ? 'text' : 'password'}
                  name="confirm"
                  autoComplete="new-password"
                  placeholder="Repeat password"
                  value={form.confirm}
                  onChange={handleChange}
                  required
                />
              </label>
            )}

            {!isRegister && (
              <div className="auth-row-between">
                <label className="auth-checkbox">
                  <input type="checkbox" /> Remember me
                </label>
                <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
              </div>
            )}

            {error && <div className="auth-error" role="alert">{error}</div>}

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Please wait…' : isRegister ? 'Create account' : 'Sign in'}
            </button>
            {isRegister && (
              <p className="auth-legal" style={{ margin: '12px 0 0', fontSize: '.8rem', lineHeight: 1.5, textAlign: 'center', opacity: .85 }}>
                By creating an account you agree to our <Link to="/terms">Terms and Conditions</Link> and <Link to="/privacy">Privacy Policy</Link>.
              </p>
            )}
          </form>
        )}

        {isStaff && staffStep === 'request' && (
          <form className="auth-form" onSubmit={handleStaffRequest} noValidate>
            <label className="auth-field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@school.com"
                value={form.email}
                onChange={handleChange}
                required
              />
            </label>
            <label className="auth-field">
              <span>School code</span>
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="ABC123"
                maxLength={6}
                value={schoolCode}
                onChange={(e) => { setSchoolCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()); if (error) setError(''); }}
                style={{ letterSpacing: '4px', textTransform: 'uppercase' }}
                required
              />
            </label>

            {error && <div className="auth-error" role="alert">{error}</div>}

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? 'Sending code…' : 'Send sign-in code'}
            </button>
            <p className="auth-tos" style={{ marginTop: 12 }}>
              We'll email you a 6-digit code to confirm it's you.
            </p>
          </form>
        )}

        {isStaff && staffStep === 'verify' && (
          <form className="auth-form" onSubmit={handleStaffVerify} noValidate>
            <p className="auth-tos" style={{ marginTop: 0, marginBottom: 14 }}>
              We sent a 6-digit code to <strong>{form.email}</strong> if it matches a staff account at that school. Check your inbox (and spam).
            </p>
            <label className="auth-field">
              <span>6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
                maxLength={6}
                value={staffOtp}
                onChange={(e) => { setStaffOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(''); }}
                style={{ letterSpacing: '8px', fontSize: '1.4rem', textAlign: 'center' }}
                required
              />
            </label>

            {error && <div className="auth-error" role="alert">{error}</div>}

            <button type="submit" className="auth-submit" disabled={loading || staffOtp.length !== 6}>
              {loading ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <p className="auth-switch" style={{ marginTop: 10 }}>
              Didn't get a code?{' '}
              <button
                type="button"
                className="auth-link auth-link-btn"
                onClick={() => { setStaffStep('request'); setStaffOtp(''); setError(''); }}
              >
                Try again
              </button>
            </p>
          </form>
        )}

        {!isStaff && (
          <p className="auth-switch">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              type="button"
              className="auth-link auth-link-btn"
              onClick={() => switchMode(isRegister ? 'login' : 'register')}
            >
              {isRegister ? 'Sign in' : 'Create one'}
            </button>
          </p>
        )}

        {!isRegister && !isStaff && (
          <p className="auth-switch">
            Are you in a school?{' '}
            <Link to="/school-portal" className="auth-link">School sign in &rarr;</Link>
          </p>
        )}

        {isStaff && (
          <p className="auth-switch">
            Need help? <Link to="/school-portal" className="auth-link">Back to school portal</Link>
          </p>
        )}

        {isRegister && (
          <p className="auth-tos">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="auth-link">Terms</Link> and{' '}
            <Link to="/privacy" className="auth-link">Privacy Policy</Link>.
          </p>
        )}
      </div>
    </div>
  );
}
